import { useEffect, useState } from 'react'
import styles from './ScoopColorPicker.module.scss'
import Sketch from '@uiw/react-color-sketch'
import { ScoopButton, ScoopMenu, ScoopTooltip, ScoopText } from '../../ui'

const presetColors = [
    '#00000000',
    '#D0021B',
    '#F5A623',
    '#F8E71C',
    '#8B572A',
    '#7ED321',
    '#417505',
    '#BD10E0',
    '#9013FE',
    '#4A90E2',
    '#50E3C2',
    '#B8E986',
    '#000000',
    '#4A4A4A',
    '#9B9B9B',
    '#FFFFFF',
]

/**
 * ScoopColorPicker - Color picker component with preset colors
 *
 * Uses ScoopMenu (Floating UI) for better positioning and performance.
 * The anchorEl state pattern follows the ScoopMenu best practices.
 *
 * @example
 * ```jsx
 * import { ScoopColorPicker } from 'components/ui'
 *
 * const MyComponent = () => {
 *   const [backgroundColor, setBackgroundColor] = useState('#ffffff')
 *
 *   return (
 *     <ScoopColorPicker
 *       value={backgroundColor}
 *       onChange={setBackgroundColor}
 *       origin="bottom"
 *       tooltip
 *       showLabel
 *     />
 *   )
 * }
 * ```
 *
 * @param {number} [width=20] - Width of the color box in pixels
 * @param {number} [height=20] - Height of the color box in pixels
 * @param {string} value - Current color value (hex format)
 * @param {'top'|'bottom'|'left'|'right'} [origin] - Menu position relative to trigger
 * @param {function} onChange - Callback when color changes (receives hex string)
 * @param {boolean} [tooltip] - Show tooltip on hover
 * @param {boolean} [showLabel] - Display color value as text label
 */
export const ScoopColorPicker = ({
    width = 20,
    height = 20,
    value,
    origin,
    onChange,
    tooltip,
    showLabel,
}) => {
    const [anchorEl, setAnchorEl] = useState(null)
    const [color, setColor] = useState(value || '#fff')

    useEffect(() => {
        setColor(value || '#fff')
    }, [value])

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        onChange(color)
        setAnchorEl(null)
    }

    const calculateLuminanceFromHex = (hex) => {
        let r = 0,
            g = 0,
            b = 0
        if (hex.length === 4) {
            r = parseInt(hex[1] + hex[1], 16)
            g = parseInt(hex[2] + hex[2], 16)
            b = parseInt(hex[3] + hex[3], 16)
        } else if (hex.length === 7) {
            r = parseInt(hex[1] + hex[2], 16)
            g = parseInt(hex[3] + hex[4], 16)
            b = parseInt(hex[5] + hex[6], 16)
        } else if (hex.length === 9) {
            r = parseInt(hex[1] + hex[2], 16)
            g = parseInt(hex[3] + hex[4], 16)
            b = parseInt(hex[5] + hex[6], 16)
        }
        const a = [r, g, b].map((v) => {
            v /= 255
            return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
        })
        return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722
    }

    const luminance = calculateLuminanceFromHex(color)

    const showBorder = luminance > 0.9 || luminance === 0
    const isTransparent = color === '#00000000'

    const getAnchorProps = () => {
        switch (origin) {
            case 'top':
                return {
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'center',
                    },
                    transformOrigin: {
                        vertical: 'bottom',
                        horizontal: 'center',
                    },
                }
            case 'bottom':
                return {
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'center',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'center',
                    },
                }
            case 'left':
                return {
                    anchorOrigin: {
                        vertical: 'center',
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }
            case 'right':
                return {
                    anchorOrigin: {
                        vertical: 'center',
                        horizontal: 'right',
                    },
                    transformOrigin: {
                        vertical: 'center',
                        horizontal: 'left',
                    },
                }
            default:
                return {
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'center',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'center',
                    },
                }
        }
    }

    const getColorBoxClassName = () => {
        const classes = [styles.colorBox]
        if (isTransparent) {
            classes.push(styles.colorBoxTransparent)
        } else if (!showBorder) {
            classes.push(styles.colorBoxNoBorder)
        }
        return classes.join(' ')
    }

    return (
        <>
            <ScoopTooltip title="Change background color" open={tooltip ? undefined : false}>
                <div className={styles.container}>
                    <ScoopButton
                        variant="text"
                        size="xxs"
                        className={styles.iconButton}
                        onClick={handleOpen}
                        style={{ padding: '4px 0' }}
                    >
                        <div
                            className={getColorBoxClassName()}
                            style={{
                                width: width,
                                height: height,
                                backgroundColor: color,
                            }}
                        />
                    </ScoopButton>
                    {showLabel && (
                        <ScoopText className={styles.label}>
                            {color === '#00000000' ? 'transparent' : color}
                        </ScoopText>
                    )}
                </div>
            </ScoopTooltip>
            <ScoopMenu
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorEl={anchorEl}
                {...getAnchorProps()}
            >
                <div
                    className={styles.colorPickerContainer}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <Sketch
                        presetColors={presetColors}
                        color={color}
                        onChange={(value) => setColor(value.hexa)}
                    />
                </div>
            </ScoopMenu>
        </>
    )
}
